import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    HostBinding,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    Type,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormArray, FormControl, NG_VALUE_ACCESSOR, NgControl, Validators} from '@angular/forms';
import {L10nTranslationService} from 'angular-l10n';
import {Decimal} from 'decimal.js';
import * as _ from 'lodash-es';
import {nanoid} from 'nanoid';
import {combineLatest, EMPTY, merge, Observable, ReplaySubject, Subject, Subscription} from 'rxjs';
import {map, pairwise, startWith, switchMap, switchMapTo, tap} from 'rxjs/operators';
import {getFormControlValueWithUpdatesStream, setControlEnabledState} from '../../../common/form-utils';
import {createNumberFormat} from '../../../common/formatters';
import {TypedSimpleChanges} from '../../../common/typed-on-changes';
import {AccountService} from '../../../components/accounts';
import {CONSTANT_CASH_LEVERAGE, isSideLeverageSelectable, LeverageService} from '../../../components/leverage';
import {
    getOrderQtyDecimalsInputParams,
    getOrderQtyFormats,
    getOrderQtyMultiCurrencyInfo,
    OrderQtyDecimalsInputParams
} from '../../../components/order-qty-multi-currency';
import {SecurityService} from '../../../components/security';
import {
    AsecurityLegsAccountDict,
    AsecurityLegsAccountDictRec,
    AspreaderSecurity,
    AspreaderSecurityLeg,
    getAsecurityLegsSecurityKeys,
    parseAsecurityConfig
} from '../../../components/security/aspreader-security';
import {
    createDisplayDecimal,
    DisplayDecimalWithCurrency,
    formatQtyWithCurrency,
    SymbolDecimalsService,
    USE_QTY_STORE_FORMAT
} from '../../../components/symbol-decimals';
import {getDefaultColumnsConfigByDef, TableColumnsSizingMode} from '../../../components/tables/table';
import {AccountType} from '../../../enums/account-type';
import {OrdType} from '../../../enums/order-type';
import {Side, SIDE_CLASSNAME_DICT, SIDE_LABEL_DICT} from '../../../enums/side';
import {UserRole} from '../../../enums/user-role';
import {DisplayExchangeAccountFactory} from '../../../factories/display-exchange-account.factory';
import {ExchangeAccountBase} from '../../../models/exchange-account';
import {Security} from '../../../models/security';
import {formatSecurityKey, isSecurityKeysEqual} from '../../../models/security-key';
import {UserService} from '../../../services/user.service';
import {
    AsecuritiesTableColumnConfig
} from '../../aspreader-securities-widget/aspreader-security-list/asecurities-table';
import {
    ASECURITY_ACCOUNTS_MAP_READONLY_TABLE_COLUMNS_SIZING,
    ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DEF,
    ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DICT,
    ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_SIZING,
    AsecurityAccountsMapTable,
    AsecurityAccountsMapTableColumnKey,
    AsecurityAccountsMapTableRow,
    AsecurityAccountsMapTableRowSide
} from './asecurity-accounts-map-table';
import {ExternalAsecurityIcebergFieldsInfo} from './external-asecurity-iceberg-fields';

const IS_DISABLED_CLASS = '-disabled';

const EMPTY_ROW = {} as AsecurityAccountsMapTableRow;

@Component({
    selector: 'asecurity-accounts-map',
    templateUrl: './asecurity-accounts-map.component.html',
    styleUrls: ['./asecurity-accounts-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AsecurityAccountsMapComponent),
            multi: true
        }
    ],
    standalone: false
})
export class AsecurityAccountsMapComponent implements OnDestroy, ControlValueAccessor, OnInit, OnChanges {

    @Input()
    public security: Security;

    @Input()
    public side: Side;

    @Input()
    public qty: number;

    @HostBinding(`class.${IS_DISABLED_CLASS}`)
    @HostBinding('attr.disabled')
    public isDisabled: boolean = null; // Null is required in order to remove "disabled" attribute.

    @Input()
    public isViewMode: boolean = false;

    @Output()
    public onMaxShowTpl = new EventEmitter<TemplateRef<unknown>>();

    @Output()
    public onMaxUnfilledTpl = new EventEmitter<TemplateRef<unknown>>();

    @ViewChild('externalMaxShowTpl', {read: TemplateRef, static: true})
    public externalMaxShowTpl: TemplateRef<unknown>;

    @ViewChild('externalMaxUnfilledTpl', {read: TemplateRef, static: true})
    public externalMaxUnfilledTpl: TemplateRef<unknown>;

    public dict: AsecurityLegsAccountDict;

    public readonly isBackoffice: boolean = this.userService.user.role === UserRole.ADMIN;

    public readonly table: AsecurityAccountsMapTable = {
        columnsDict:        ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DICT,
        columnsConfig:      getDefaultColumnsConfigByDef(ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DEF)
                                .filter(columnConfig =>

                                    // Show Price Ratio only in BO
                                    columnConfig.key !== AsecurityAccountsMapTableColumnKey.PRICE_RATIO
                                    || this.isBackoffice
                                ),
        columnsSizingSteps: ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_SIZING,
        columnsSizingMode:  TableColumnsSizingMode.AUTO,
        rows:               [],
        emptyRow:           EMPTY_ROW,
        disableEmptyRows:   true,
        columnsUpdate$:     new Subject<AsecuritiesTableColumnConfig[]>()
    };

    public isLeverageAvailable: boolean = false;

    public readonly id = nanoid();

    public externalIcebergFieldsInfo: ExternalAsecurityIcebergFieldsInfo;

    private outerPropagateOnChange: (value: any) => void;

    private ngControl: NgControl;

    private form: FormArray;

    private rowsHandingSubscription: Subscription;

    private readonly sideQtyChanged$ = new ReplaySubject<void>(1);

    private readonly priceRatioFormat: Intl.NumberFormat = createNumberFormat({
        isDecimalRequired: false,
        decimalCount:      2
    });

    constructor(
        private injector: Injector,
        private securityService: SecurityService,
        private leverageService: LeverageService,
        private symbolDecimalsService: SymbolDecimalsService,
        private accountService: AccountService,
        private translationService: L10nTranslationService,
        private userService: UserService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    public writeValue(dict: AsecurityLegsAccountDict): void {
        this.dict = dict;
        this.updateTable();
        this.changeDetectorRef.markForCheck();
    }

    public propagateChange(dict: AsecurityLegsAccountDict): void {
        if (this.outerPropagateOnChange) {
            this.outerPropagateOnChange(dict);
        }
    }

    public registerOnChange(handler: (value: any) => void): void {
        this.outerPropagateOnChange = handler;
    }

    public registerOnTouched(handler: (value: any) => void): void {
        // Not implemented.
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = !!isDisabled || null; // Null is required in order to remove "disabled" attribute.

        if (this.form) {
            setControlEnabledState(this.form, !isDisabled);
        }

        this.table.columnsSizingSteps = isDisabled
            ? ASECURITY_ACCOUNTS_MAP_READONLY_TABLE_COLUMNS_SIZING
            : ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_SIZING;

        this.changeDetectorRef.markForCheck();
    }

    public ngOnInit(): void {
        this.ngControl = this.injector.get<NgControl>(NgControl as Type<NgControl>, undefined, {self: true});
        this.sideQtyChanged$.next();

        // Workaround for ExpressionChangedAfterItHasBeenCheckedError
        Promise.resolve().then(() => {
            this.onMaxShowTpl.next(this.externalMaxShowTpl);
            this.onMaxUnfilledTpl.next(this.externalMaxUnfilledTpl);
        });
    }

    public ngOnChanges(changes: TypedSimpleChanges<AsecurityAccountsMapComponent>): void {
        if (changes.security && this.ngControl) {
            this.writeValue(this.ngControl.value);
        }

        if (
            changes.side && !changes.side.isFirstChange()
            || changes.qty && !changes.qty.isFirstChange()
        ) {
            this.sideQtyChanged$.next();
        }
    }

    public ngOnDestroy(): void {
        if (this.rowsHandingSubscription) {
            this.rowsHandingSubscription.unsubscribe();
            this.rowsHandingSubscription = undefined;
        }

        // Workaround for ExpressionChangedAfterItHasBeenCheckedError
        Promise.resolve().then(() => {
            this.onMaxShowTpl.next(undefined);
            this.onMaxUnfilledTpl.next(undefined);
        });
    }

    private updateTable(): void {
        if (this.rowsHandingSubscription) {
            this.rowsHandingSubscription.unsubscribe();
        }

        this.rowsHandingSubscription = new Subscription();

        const asecurity = this.security as AspreaderSecurity;
        const asecurityConfig = asecurity && parseAsecurityConfig(asecurity);

        if (asecurityConfig) {
            const legs = asecurityConfig.legs;
            const securityKeys = getAsecurityLegsSecurityKeys(legs);

            const controls: FormControl[] = [];
            const rows: AsecurityAccountsMapTableRow[] = [];

            const rowsHandlingStreams: Array<Observable<any>> = [];

            securityKeys.forEach(

                // tslint:disable:cyclomatic-complexity
                securityKey => {
                    const securityKeyStr = formatSecurityKey(securityKey);
                    const rec: AsecurityLegsAccountDictRec = this.dict && this.dict[securityKeyStr] || null;
                    const leg: AspreaderSecurityLeg = legs
                        .find(itLeg => isSecurityKeysEqual(itLeg.securityKey, securityKey));

                    const accountControl = new FormControl(
                        {
                            value:    rec && rec.accountBase || null,
                            disabled: !!this.isDisabled
                        },
                        Validators.required
                    );

                    const leverageControl = new FormControl(
                        {
                            value:    rec && rec.leverage || null,
                            disabled: !!this.isDisabled
                        },
                        Validators.required
                    );

                    const row: AsecurityAccountsMapTableRow = {
                        leg,
                        securityKey,
                        securityKeyStr,
                        accountControl,
                        leverageControl,
                        accountFilter:     account => account.exchange === securityKey.securityExchange,
                        isMakerOnly:       !!(rec && rec.makerOnly),
                        displayPriceRatio: createDisplayDecimal(leg.priceRatio, this.priceRatioFormat),

                        /**
                         * To be initialized/updated in {@link createRowProcessingStream}.
                         */
                        security:           undefined,
                        displaySide:        this.createDisplaySide(leg, this.side),
                        isLeverageAllowed:  !!(this.isViewMode && leverageControl.value),
                        qtyFormatter:       undefined,
                        displayAccountName: undefined,
                        qty:                this.getRowQty(leg, this.qty)
                    };

                    controls.push(accountControl, leverageControl);
                    rows.push(row);

                    if (leg.activeQuoting) {
                        const maxShowControl = new FormControl({
                            value:    rec && rec.maxShow || null,
                            disabled: !!this.isDisabled
                        });
                        const maxUnfilledControl = new FormControl({
                            value:    rec && rec.maxUnfilled || null,
                            disabled: !!this.isDisabled
                        });

                        this.externalIcebergFieldsInfo = {
                            maxShow:     {
                                formControl: maxShowControl,

                                /**
                                 * To be initialized/updated in {@link createRowProcessingStream}.
                                 */
                                qtyInputParams: undefined,
                                qtyFormatter:   undefined,
                                currency:       undefined
                            },
                            maxUnfilled: {
                                formControl: maxUnfilledControl,

                                /**
                                 * To be initialized/updated in {@link createRowProcessingStream}.
                                 */
                                qtyInputParams: undefined,
                                qtyFormatter:   undefined,
                                currency:       undefined
                            }
                        };

                        row.maxShowControl = maxShowControl;
                        row.maxUnfilledControl = maxUnfilledControl;
                        controls.push(maxShowControl, maxUnfilledControl);
                    }

                    if (!this.isViewMode) {
                        rowsHandlingStreams.push(this.createRowProcessingStream(row));
                    }
                }
            );

            this.form = new FormArray(controls);
            this.table.rows = rows;

            merge(...rowsHandlingStreams).subscribe();

            this.form.valueChanges.pipe(
                tap(() => this.propagateChangeByForm())
            ).subscribe();
        } else {
            this.table.rows = [];
            this.form = new FormArray([]);
        }

        this.updateOverallLeverageAvailability();
    }

    private createRowProcessingStream(row: AsecurityAccountsMapTableRow): Observable<any> {
        const {securityKey, accountControl, leverageControl, leg} = row;

        const securityProcessing$ = this.securityService.getSecurityBySecurityKey(securityKey).pipe(

            /**
             * Security can be empty here, if it's inactive (e.g. outdated futures/options).
             * Although inactive securities can be returned by API (including streaming by SECURITY_INFO),
             * inactive securities are filtered out by {@link SecurityService}.
             */
            tap(security => row.security = security)
        );

        const updateLeverage = (security: Security, accountBase: ExchangeAccountBase, side: Side) =>
            this.leverageService.getSideLeveragesForSecurityAcc(
                security,
                accountBase,
                side
            ).pipe(
                tap<number[]>(sideLeverages => {
                    const isLeverageAllowed = isSideLeverageSelectable(sideLeverages);
                    row.isLeverageAllowed = isLeverageAllowed;

                    if (isLeverageAllowed) {
                        const value = sideLeverages.includes(leverageControl.value)
                            ? leverageControl.value
                            : sideLeverages[0];

                        leverageControl.setValue(value);
                    }

                    setControlEnabledState(leverageControl, !this.isDisabled && isLeverageAllowed);
                    this.updateOverallLeverageAvailability(isLeverageAllowed);
                })
            );

        return securityProcessing$.pipe(
            switchMapTo(
                combineLatest([
                    getFormControlValueWithUpdatesStream<ExchangeAccountBase>(accountControl),
                    this.sideQtyChanged$
                ])
            ),
            map(([accountBase]) => ({
                accountBase,
                side: this.side,
                qty:  this.qty
            })),
            startWith(undefined),
            pairwise(),

            // tslint:disable:cyclomatic-complexity
            switchMap(([prev, current]) => {
                const isSideChanged = !prev || prev.side !== current.side;
                const isAccountChanged = !prev || prev.accountBase !== current.accountBase;
                const isQtyChanged = !prev || prev.qty !== current.qty;
                const hasChanges: boolean = isSideChanged || isAccountChanged || isQtyChanged;

                if (isSideChanged) {
                    row.displaySide = this.createDisplaySide(leg, current.side);
                }

                if (isAccountChanged || isSideChanged) {
                    Object.assign(
                        row,
                        this.createRowQtyFormatter(row.leg, row.security, current.accountBase, current.side)
                    );

                    if (leg.activeQuoting) {
                        this.externalIcebergFieldsInfo.maxShow.qtyInputParams = row.qtyInputParams;
                        this.externalIcebergFieldsInfo.maxShow.qtyFormatter = row.qtyFormatter;
                        this.externalIcebergFieldsInfo.maxShow.currency = row.qtyCurrency;
                        this.externalIcebergFieldsInfo.maxUnfilled.qtyInputParams = row.qtyInputParams;
                        this.externalIcebergFieldsInfo.maxUnfilled.qtyFormatter = row.qtyFormatter;
                        this.externalIcebergFieldsInfo.maxUnfilled.currency = row.qtyCurrency;
                    }
                }

                if (isAccountChanged) {
                    row.displayAccountName = current.accountBase
                        ? DisplayExchangeAccountFactory.getDisplayAccountName(
                            this.accountService.getAccountByIdFromCache(current.accountBase.id),
                            true,
                            current.accountBase.type,
                            this.translationService
                        )
                        : '';
                }

                if (isQtyChanged) {
                    row.qty = this.getRowQty(leg, current.qty);
                }

                if (hasChanges) {
                    this.changeDetectorRef.markForCheck();
                }

                return (isAccountChanged || isSideChanged)
                    ? updateLeverage(row.security, current.accountBase, current.side)
                    : EMPTY;
            })
        );
    }

    private updateOverallLeverageAvailability(isAnywhereAvailable: boolean = false): void {
        const LEVERAGE_COLUMN_KEY = AsecurityAccountsMapTableColumnKey.LEVERAGE;
        const leverageColumnIndex = this.table.columnsConfig
            .findIndex(columnConfig => columnConfig.key === LEVERAGE_COLUMN_KEY);

        const isLeverageAvailable = !!isAnywhereAvailable
            || !this.table.rows.every(row => !row.isLeverageAllowed);

        this.isLeverageAvailable = isLeverageAvailable;

        if (isLeverageAvailable) {
            if (leverageColumnIndex < 0) {
                this.table.columnsConfig = [
                    ...this.table.columnsConfig,
                    {
                        key:   LEVERAGE_COLUMN_KEY,
                        width: undefined
                    }
                ];
            }
        } else {
            if (leverageColumnIndex >= 0) {
                this.table.columnsConfig = [...this.table.columnsConfig];
                this.table.columnsConfig.splice(leverageColumnIndex, 1);
            }
        }

        this.changeDetectorRef.markForCheck();
    }

    private propagateChangeByForm(): void {
        const newDict: AsecurityLegsAccountDict = {...this.dict};

        this.table.rows.forEach(row => {
            const accountBase = row.accountControl.value;
            const origRec: AsecurityLegsAccountDictRec = this.dict && this.dict[row.securityKeyStr];

            newDict[row.securityKeyStr] = {
                accountBase,
                leverage:    accountBase && accountBase.type === AccountType.MARGIN
                                 ? row.leverageControl.value
                                 : CONSTANT_CASH_LEVERAGE,
                makerOnly:   origRec && origRec.makerOnly || false,
                maxShow:     row.maxShowControl ? row.maxShowControl.value : undefined,
                maxUnfilled: row.maxUnfilledControl ? row.maxUnfilledControl.value : undefined
            };
        });

        this.propagateChange(newDict);
    }

    private createDisplaySide(leg: AspreaderSecurityLeg, orderSide: Side): AsecurityAccountsMapTableRowSide {
        const getSideSign = (side: Side): number =>
            (side === Side.SELL) ? -1 : 1;

        const origLegSide: Side = leg.side;
        const resultSide: Side = (getSideSign(origLegSide) * getSideSign(orderSide) > 0)
            ? Side.BUY
            : Side.SELL;

        return {
            origLegSide,
            orderSide,
            resultSide,
            label:   SIDE_LABEL_DICT[resultSide],
            classes: SIDE_CLASSNAME_DICT[resultSide]
        };
    }

    private getRowQty(leg: AspreaderSecurityLeg, orderQty: number): number {
        return _.isFinite(orderQty)
            ? Decimal.mul(orderQty, leg.qtyRatio).toNumber()
            : undefined;
    }

    private createRowQtyFormatter(
        leg: AspreaderSecurityLeg,
        legSecurity: Security,
        accountBase: ExchangeAccountBase,
        side: Side
    ): Pick<AsecurityAccountsMapTableRow, 'qtyFormatter' | 'qtyInputParams' | 'qtyCurrency'> {
        let result: Pick<AsecurityAccountsMapTableRow, 'qtyFormatter' | 'qtyInputParams' | 'qtyCurrency'>;

        /**
         * Security can be empty here, if it's inactive (e.g. outdated futures/options).
         * Although inactive securities can be returned by API (including streaming by SECURITY_INFO),
         * inactive securities are filtered out by {@link SecurityService}.
         */
        if (legSecurity) {
            const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfo(legSecurity, {
                accountType: accountBase && accountBase.type,
                orderType:   OrdType.LIMIT, // Always limit for Autospreader
                side
            });

            const securityFormats = this.symbolDecimalsService.getSecurityFormats(legSecurity);
            const securityInputParams = this.symbolDecimalsService.getSecurityInputParams(legSecurity);

            const qtyFormats = getOrderQtyFormats({
                security:     legSecurity,
                qtyMultiCurrencyInfo,
                securityFormats,
                securityInputParams,
                qtyAssetType: qtyMultiCurrencyInfo.baseQtyAssetType
            });

            const qtyBaseFormatter = (value: number): DisplayDecimalWithCurrency =>
                formatQtyWithCurrency(value, qtyFormats.baseCurrencySymbol, qtyFormats.baseFormats);

            const qtyInputParams: OrderQtyDecimalsInputParams = leg.activeQuoting
                ? getOrderQtyDecimalsInputParams({
                    qtyMultiCurrencyInfo,
                    securityInputParams
                })
                : undefined;

            result = {
                qtyFormatter: qtyBaseFormatter,
                qtyInputParams,
                qtyCurrency:  qtyFormats.baseCurrencySymbol
            };
        } else {

            /**
             * In case of unknown security, format numbers with decimal digits,
             * when it's needed, but no trailing decimal zeroes.
             */
            const securityFormats = this.symbolDecimalsService.getSecurityFormats(undefined, true);

            result = {
                qtyFormatter: value => formatQtyWithCurrency(value, '', securityFormats, USE_QTY_STORE_FORMAT)
            };
        }

        return result;
    }

}
